import moment from "moment";
import { api } from "../api";

export enum NotificationType {
  ReportApproved = 1,
  Program = 2,
  Payments = 3,
  Comments = 4,
  Certificate = 5,
  ReportRejected = 6,
  ReportInfo = 7,
  SecurityAlert = 8,
  SystemMaintenance = 9,
  FeatureUpdate = 10,
  Reminder = 11,
  Collaboration = 12,
  Achievement = 13,
  PaymentReminder = 14,
  ReportDeadline = 15,
  BugReport = 16,
  SupportTicket = 17,
  ComplianceAlert = 18,
  Training = 19,
  Community = 20,
  AssetCreated = 21,
  ScopeVerification = 22
}

export enum NotificationPriority {
  Low = "1",
  Normal = "2", 
  High = "3",
  Critical = "4"
}

export enum NotificationCategory {
  Security = "security",
  System = "system",
  Business = "business",
  Personal = "personal",
  Collaboration = "collaboration",
  Compliance = "compliance",
  Training = "training"
}

export enum NotificationStatus {
  Pending = "pending",
  Sent = "sent",
  Delivered = "delivered",
  Read = "read",
  Failed = "failed"
}

export type NotificationAction = {
  label: string;
  action: string;
  url?: string;
  method?: "GET" | "POST" | "PUT" | "DELETE";
  data?: Record<string, any>;
  style?: "primary" | "secondary" | "danger" | "success";
};

type NotificationAPIResponse = {
  notification_id: number;
  type: NotificationType;
  category: NotificationCategory;
  priority: NotificationPriority;
  status: NotificationStatus;
  title: string;
  message: string;
  rich_content?: string;
  payload: string;
  actions?: NotificationAction[];
  metadata?: string;
  read: boolean;
  read_at?: string;
  expires_at?: string;
  scheduled_for?: string;
  createdAt: string;
  priority_label: string;
  category_label: string;
  status_label: string;
};

export type CTBNotification = {
  id: number;
  type: NotificationType;
  category: NotificationCategory;
  priority: NotificationPriority;
  status: NotificationStatus;
  title: string;
  message: string;
  rich_content?: string;
  payload: number | string;
  actions?: NotificationAction[];
  metadata?: string;
  read: boolean;
  read_at?: string;
  expires_at?: string;
  scheduled_for?: string;
  createdAt: string;
  priority_label: string;
  category_label: string;
  status_label: string;
};

type NotificationStatsResponse = {
  total: number;
  unread: number;
  highPriority?: number;
  actionRequired?: number;
  breakdown: Array<{
    read: boolean;
    category: string;
    priority: number;
    status: string;
    count: string;
  }>;
};

type NotificationsListResponse = {
  notifications: NotificationAPIResponse[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
};

const notificationsUrl = "/v2/notifications";

/**
 * The Notifications API slice definition.
 *
 * Endpoints for interacting and reading user notifications
 * are defined here.
 */
export const notifcationsApi = api.injectEndpoints({
  endpoints: builder => ({
    /**
     * Retrieve the current list of notifications
     * for the current user with filtering and pagination
     */
    getNotifications: builder.query<{ 
      notifications: CTBNotification[]; 
      total: number; 
      limit: number; 
      offset: number; 
      hasMore: boolean; 
    }, {
      read?: boolean;
      category?: string;
      priority?: string;
      limit?: number;
      offset?: number;
      sort?: string;
      type?: number;
      action_required?: boolean;
      high_priority?: boolean;
    }>({
      query: (params) => ({ 
        url: notificationsUrl, 
        method: "get",
        params 
      }),
      transformResponse: (response: NotificationsListResponse): { 
        notifications: CTBNotification[]; 
        total: number; 
        limit: number; 
        offset: number; 
        hasMore: boolean; 
      } => ({
        ...response,
        notifications: response.notifications.map(notification => ({
          ...notification,
          id: notification.notification_id,
          payload: (notification.payload && /^\d+$/.test(notification.payload))
            ? parseInt(notification.payload, 10)
            : notification.payload,
          createdAt: moment(notification.createdAt).fromNow()
        }))
      }),
      providesTags: ["Notifications"]
    }),

    /**
     * Get notification statistics
     */
    getNotificationStats: builder.query<NotificationStatsResponse, void>({
      query: () => ({ url: `${notificationsUrl}/stats`, method: "get" }),
      providesTags: ["Notifications"]
    }),

    /**
     * Mark the given notifications as read
     */
    markNotifications: builder.mutation<void, {
      ids: number[];
      read?: boolean;
      status?: string;
      action_clicked?: boolean;
    }>({
      query: (data) => ({ 
        url: notificationsUrl, 
        method: "post", 
        data 
      }),
      invalidatesTags: ["Notifications"]
    }),

    /**
     * Mark all notifications as read
     */
    markAllNotificationsAsRead: builder.mutation<void, void>({
      query: () => ({ 
        url: `${notificationsUrl}/mark-all-read`, 
        method: "post" 
      }),
      invalidatesTags: ["Notifications"]
    }),

    /**
     * Mark all notifications as unread
     */
    markAllNotificationsAsUnread: builder.mutation<void, void>({
      query: () => ({ 
        url: `${notificationsUrl}/mark-all-unread`, 
        method: "post" 
      }),
      invalidatesTags: ["Notifications"]
    }),

    /**
     * Clear (delete) the given notifications
     */
    clearNotifications: builder.mutation<void, number[]>({
      query: ids => ({
        url: notificationsUrl,
        method: "delete",
        data: { ids }
      }),
      invalidatesTags: ["Notifications"]
    }),

    /**
     * Clear all notifications (soft delete all)
     */
    clearAllNotifications: builder.mutation<void, void>({
      query: () => ({
        url: `${notificationsUrl}/clear-all`,
        method: "delete"
      }),
      invalidatesTags: ["Notifications"]
    })
  })
});

export const {
  useGetNotificationsQuery,
  useGetNotificationStatsQuery,
  useMarkNotificationsMutation,
  useMarkAllNotificationsAsReadMutation,
  useMarkAllNotificationsAsUnreadMutation,
  useClearNotificationsMutation,
  useClearAllNotificationsMutation
} = notifcationsApi;
